import { assertNever } from "@impero-com/common/utils/obj";

import {
    AdminEditViewAccessRights,
    StandardAccessRight,
    UserOverview,
    SavedReportViewListDto,
    SavedReportResources,
    SavedReportViewDtoContent,
} from "api/generated/model";
import {
    LegacyClassificationUsers,
    LegacyClassificationsForUser,
    LegacyDataSheetItem,
    LegacySavedReportUsers,
    LegacyUser,
} from "api/legacy/model";

import { ReportColumnId, REPORT_COLUMN_IDS } from "./list/columns";
import {
    ClassificationSavedReport,
    Report,
    ClassificationsForUser,
    AccessRightsEditorData,
} from "./types";

function convertLegacyDataSheetReportToReport(
    legacyDataSheetItem: LegacyDataSheetItem
): Report {
    return {
        type: "Datasheet",
        data: {
            dataSheetId: legacyDataSheetItem.DataSheetId,
            dataSheetName: legacyDataSheetItem.DataSheetName,
            controlProgramId: legacyDataSheetItem.ControlProgramId,
            controlProgramName: legacyDataSheetItem.ControlProgramName,
            archived: legacyDataSheetItem.Archived,
        },
    };
}

function convertLegacyClassificationsForUserToClassificationsForUser(
    legacyClassificationsForUser: LegacyClassificationsForUser
): ClassificationsForUser {
    return {
        classifications:
            legacyClassificationsForUser.ClassificationsAndRights.map(
                (classificationAndRights) => ({
                    classification: {
                        id: classificationAndRights.Classification.Id,
                        title: classificationAndRights.Classification.Title,
                        description:
                            classificationAndRights.Classification.Description,
                    },
                    rights: {
                        canEdit: classificationAndRights.CanEdit,
                        canEditAccessRights:
                            classificationAndRights.CanEditAccessRights,
                        canDelete: classificationAndRights.CanDelete,
                    },
                })
            ),
        canCreate: legacyClassificationsForUser.CanCreate,
    };
}

function convertSavedReportListDtoToReports(
    listDto: SavedReportViewListDto
): Array<Report> {
    const { content: savedReportsDto, linkedResources } = listDto;

    return savedReportsDto.map((reportDto) =>
        convertSavedReportDtoToReport(reportDto, linkedResources)
    );
}

function convertSavedReportDtoToReport(
    reportDto: SavedReportViewDtoContent,
    linkedResources: SavedReportResources
): Report {
    const { resource: savedReport, actions } = reportDto;

    const {
        reportType,
        id,
        title,
        description = null,
        pushReportConfigurationCount,
    } = savedReport;
    const usersByAccessRight = convertAccessRightsToUsersMap(
        savedReport.accessRights,
        linkedResources.users
    );

    switch (reportType.type) {
        case "Status":
            return {
                type: "Status",
                data: {
                    id,
                    title,
                    description,
                    pushReportConfigurationCount,
                    actions,
                    usersByAccessRight,
                },
            } satisfies Report;
        case "Workload":
            return {
                type: "Workload",
                data: {
                    id,
                    title,
                    description,
                    pushReportConfigurationCount,
                    actions,
                    usersByAccessRight,
                },
            } satisfies Report;
        case "Classification":
            const classificationId = reportType.data.classificationId;
            const classification =
                linkedResources.classifications[classificationId];
            if (!classification) {
                throw new Error(
                    `Classification (id=${classificationId}) not found in linkedResources`
                );
            }
            return {
                type: "Classification",
                data: {
                    id,
                    title,
                    description,
                    pushReportConfigurationCount,
                    classification: {
                        ...classification,
                        description: classification.description ?? null,
                    },
                    actions,
                    usersByAccessRight,
                } satisfies ClassificationSavedReport,
            } satisfies Report;
        default:
            return assertNever(reportType);
    }
}

function convertAccessRightsToUsersMap(
    accessRights: AdminEditViewAccessRights,
    users: Record<number, UserOverview>
): Map<StandardAccessRight, Array<UserOverview>> {
    const getUserById = (userId: number): UserOverview => {
        const user = users[userId];
        if (!user) {
            throw new Error(`User (id=${userId}) not found in linkedResources`);
        }
        return user;
    };
    return new Map([
        ["Admin", accessRights.admin.map(getUserById)],
        ["Edit", accessRights.edit.map(getUserById)],
        ["View", accessRights.view.map(getUserById)],
    ]);
}

function legacyUserToUserOverview(user: LegacyUser): UserOverview {
    return {
        id: user.Id,
        firstname: user.Firstname,
        lastname: user.Lastname,
        email: user.Email,
    };
}

/**
 * Converts a legacy EditAccessRights GET response (saved report or
 * classification — both share this shape) into what the
 * {@link RHFAccessRights} editor expects.
 */
function convertLegacyAccessUsersToEditorData(
    legacy: LegacySavedReportUsers | LegacyClassificationUsers
): AccessRightsEditorData {
    return {
        users: [
            ...legacy.UsersWithAdminAccess,
            ...legacy.UsersWithEditAccess,
            ...legacy.UsersWithViewAccess,
        ].map(legacyUserToUserOverview),
        accessRights: {
            admin: legacy.UsersWithAdminAccess.map((user) => user.Id),
            edit: legacy.UsersWithEditAccess.map((user) => user.Id),
            view: legacy.UsersWithViewAccess.map((user) => user.Id),
        },
    };
}

function isReportColumnId(column: string): column is ReportColumnId {
    const allColumnIdsAsStrings: Array<string> =
        Object.values(REPORT_COLUMN_IDS);
    return allColumnIdsAsStrings.includes(column);
}

function convertStringToReportColumnIdOrThrow(column: string): ReportColumnId {
    if (isReportColumnId(column)) {
        return column;
    } else {
        throw new Error(`Invalid ReportColumnId: ${column}`);
    }
}

export {
    convertLegacyClassificationsForUserToClassificationsForUser,
    convertLegacyDataSheetReportToReport,
    convertSavedReportListDtoToReports,
    convertLegacyAccessUsersToEditorData,
    isReportColumnId,
    convertStringToReportColumnIdOrThrow,
};
